import Shepherd from 'shepherd.js';
import type { Step, StepOptions } from 'shepherd.js/step';
import { AutomaticReserveApiApiAutomaticReserveCountGet } from '@clients/PricetrackApi';
import {
    type FloatingReserveGeneralResponse,
    ReserveApiApiReserveFloatingGetActiveFloatingReservesGet,
} from '@clients/HotelApi';
import { ReserveType } from '@utils/itinerary';

const capacityTrackTour = new Shepherd.Tour({
    tourName: 'view-page-capacity-tracking',
    useModalOverlay: true,
    defaultStepOptions: {
        scrollTo: {
            behavior: 'smooth',
            block: 'center',
        },
        title: 'رهگیری ظرفیت',
        cancelIcon: {
            enabled: true,
        },
    },
});

export class LayoutStore {
    //#region state
    inTimeout = false;

    ssrIsShowLoadingModal = false;

    /**
     * check is redirected user from safarmarket or not
     */
    isRedirectFromSafarmarket = false;

    autoReserveInProcessCount = 0;

    inProcessFloatings: FloatingReserveGeneralResponse[] = [];

    /**
     * @deprecated use useDevice().isWebApp instead
     */
    isWebApp = false;

    private activeMobileNav = true;

    private justGetAutoReserveInProcessCount = false;

    isMounted = false;

    //#endregion

    get mobileNavActiveState() {
        return this.activeMobileNav;
    }

    get capacityTrackTour() {
        return capacityTrackTour;
    }

    get pathTrackingStepKey() {
        return 'capacity-path-tracking-step';
    }

    get autoReserveStepKey() {
        return 'auto-reserve-step';
    }

    get capacityTrackTourStorageKey() {
        return 'showed-capacity-track-walkthrough';
    }

    get isExtraFeaturesEnabled() {
        return useWhiteLabel().isMrbilit.value;
    }

    get normalSupportNumber() {
        const { featureFlags } = storeToRefs(usePostHog());
        if (!this.isMounted || !featureFlags.value['support-backup-enabled']) {
            return '61169000';
        }
        return '91692545';
    }

    get organizationalSupportNumber() {
        const { featureFlags } = storeToRefs(usePostHog());
        if (
            !this.isMounted ||
            !featureFlags.value?.['support-backup-enabled']
        ) {
            return '61169999';
        }
        return '91692545';
    }

    get supportNumber() {
        const authStore = useAuthStore();
        if (this.isMounted && authStore.isOrganizationalUser) {
            return this.organizationalSupportNumber;
        }
        return this.normalSupportNumber;
    }

    capacityTourAddStep({
        step,
        index,
    }: {
        step: Step | StepOptions;
        index?: number;
    }) {
        this.capacityTrackTour.removeStep(step.id!);
        this.capacityTrackTour.addStep(step, index);
    }

    removeMobileNav() {
        this.activeMobileNav = false;
    }

    showMobileNav() {
        this.activeMobileNav = true;
    }

    clearSteps() {
        this.capacityTrackTour.steps.forEach(step =>
            this.capacityTrackTour.removeStep(step.id)
        );
    }

    startCapacityTrackTour(timeout: number) {
        if (this.inTimeout) {
            return;
        }
        this.inTimeout = true;
        setTimeout(() => {
            this.inTimeout = false;
            if (this.capacityTrackTour.steps.length > 0) {
                const onceShowed = localStorage.getItem(
                    this.capacityTrackTourStorageKey
                );
                if (!onceShowed && !this.capacityTrackTour.isActive()) {
                    localStorage.setItem(
                        this.capacityTrackTourStorageKey,
                        'true'
                    );
                    this.capacityTrackTour.steps.forEach(step =>
                        useAnalyticsStore().trackPosthog(`show-${step.id}`)
                    );
                    this.capacityTrackTour.start();
                }
            }
        }, timeout);
    }

    get showSuggestedBg() {
        return useSearchFormStore().reserveType !== ReserveType.Hotel;
    }

    async getAutoReserveInProcessCount() {
        if (!useAuthStore().loggedIn || this.justGetAutoReserveInProcessCount) {
            return;
        }
        this.justGetAutoReserveInProcessCount = true;
        const { data } = await AutomaticReserveApiApiAutomaticReserveCountGet();
        this.autoReserveInProcessCount = data.inProcessTripCounts || 0;
        setTimeout(() => {
            this.justGetAutoReserveInProcessCount = false;
        }, 30000);
    }

    async getFloatingsInProcess() {
        if (!useAuthStore().loggedIn) {
            return;
        }
        try {
            const { data } =
                await ReserveApiApiReserveFloatingGetActiveFloatingReservesGet();
            this.inProcessFloatings = data || [];
        } catch (e) {
            reportException(e);
        }
    }
}

export const { use: useLayoutStore } = defineClassStore(
    'layout',
    () => new LayoutStore()
);
